// @ts-nocheck — admin page, types added in a later sub-step
import React from "react";
import { Navigate } from "react-router-dom";
import { listAuditEntries, listUsers } from "../lib/data";
import type { AuditEntry, User } from "../lib/data";
// Phase 3d — status-semantics: pair every scope color with an icon so the
// signal isn't color-only (audit §4).
import { Plus, Pencil, UserMinus, KeyRound, Shield, Minus } from "../icons";
// Phase 3e — section-level empty/error/loading states.
import EmptyState from "../components/ui/EmptyState";
import ErrorState from "../components/ui/ErrorState";
import Skeleton from "../components/ui/Skeleton";
// Phase 4b audit — DR-21 (Button) + DR-23 (DataTable) compliance.
import Button from "../components/ui/Button";
import DataTable from "../components/ui/DataTable";
// Phase B1 — layout primitive.
import Page from "../components/ui/Page";
// Phase 4e — IT/EN parity. The audit log was English-only; reads the
// global lang preference set by ForecastItaly's lang toggle.
import { useLang } from "../lib/lang";
// Hotfix (2026-05-16) — shared validated worker URL resolver.
import { readWorkerUrl } from "../lib/worker-url";

/* src/views/AdminAudit.tsx — /admin/audit (Phase 5.7b).

   Read-only browser for the append-only audit log written by the worker
   on every state/user mutation. Open per §1.4: anyone with the view
   password can see who changed what.

   What it shows: time, operator (resolved to a user name when possible),
   scope (state.full / state.partial.* / user.* / auth.view-password),
   changed fields, and before/after hashes (8-char FNV-1a — same content =
   same hash, so visual diff at a glance).

   What it does NOT do:
     - No filters yet (just newest-first + load-older). Filters by user /
       scope / date land in a follow-up if asked.
     - No write surface — the log is append-only on the worker.
*/

const { useState, useEffect, useMemo } = React;

const PAGE_SIZE = 50;

// readWorkerUrl moved to ../lib/worker-url.ts (allowlist + self-heal).

function readViewPw() {
  try { return localStorage.getItem("forecast_view_pw") || ""; }
  catch { return ""; }
}

function fmtTs(ts: string) {
  try {
    const d = new Date(ts);
    return d.toLocaleString();
  } catch {
    return ts;
  }
}

const SCOPE_TONE: Record<string, string> = {
  "state.full":          "var(--danger)",
  "state.partial.admin": "var(--accent)",
  "state.partial.owner": "var(--text-primary)",
  "user.create":         "var(--success)",
  "user.update":         "var(--accent)",
  "user.deactivate":     "var(--danger)",
  "auth.view-password":  "var(--danger)",
};

// Phase 3d — icon-per-scope so the signal pairs with the SCOPE_TONE color.
// Each icon's stroke is inherited via currentColor from the cell's color
// rule, so the visual reads as "<icon> <scope>" in the same hue.
function scopeIcon(scope: string) {
  switch (scope) {
    case "state.full":          return <Shield     size={14} />;
    case "state.partial.admin": return <Shield     size={14} />;
    case "state.partial.owner": return <Pencil     size={14} />;
    case "user.create":         return <Plus       size={14} />;
    case "user.update":         return <Pencil     size={14} />;
    case "user.deactivate":     return <UserMinus  size={14} />;
    case "auth.view-password":  return <KeyRound   size={14} />;
    default:                    return null;
  }
}

export default function AdminAudit() {
  if (!readViewPw()) {
    return <Navigate to="/forecast/italy" replace />;
  }

  const lang = useLang();
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [usersById, setUsersById] = useState<Record<string, User>>({});
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [exhausted, setExhausted] = useState(false);

  // Resolve operator ids to display names. Users are openly readable.
  const loadUsers = async () => {
    try {
      const list = await listUsers({ workerUrl: readWorkerUrl(), viewPassword: readViewPw() });
      const map: Record<string, User> = {};
      for (const u of list) map[u.id] = u;
      setUsersById(map);
    } catch (e) {
      // Non-fatal — table renders raw ids if user fetch fails.
    }
  };

  const loadInitial = async () => {
    setBusy(true);
    setLoadError(null);
    setExhausted(false);
    try {
      const list = await listAuditEntries({
        workerUrl: readWorkerUrl(),
        viewPassword: readViewPw(),
        limit: PAGE_SIZE,
      });
      setEntries(list);
      if (list.length < PAGE_SIZE) setExhausted(true);
    } catch (e: any) {
      setLoadError(e?.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  const loadOlder = async () => {
    if (entries.length === 0 || exhausted) return;
    const oldest = entries[entries.length - 1];
    // Worker returns entries with ts >= since. Stepping back: we re-fetch
    // a window ending at the oldest entry we have, then de-dup. Cheap.
    setBusy(true);
    try {
      const list = await listAuditEntries({
        workerUrl: readWorkerUrl(),
        viewPassword: readViewPw(),
        limit: PAGE_SIZE * 2,
      });
      // Filter to entries strictly OLDER than what we already have.
      const seen = new Set(entries.map(e => `${e.ts}|${e.beforeHash}|${e.afterHash}`));
      const older = list.filter(e => !seen.has(`${e.ts}|${e.beforeHash}|${e.afterHash}`));
      if (older.length === 0) setExhausted(true);
      else setEntries([...entries, ...older]);
    } catch (e: any) {
      setLoadError(e?.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { loadInitial(); loadUsers(); }, []);

  const rows = useMemo(() => entries.map(e => {
    const u = usersById[e.userId];
    return {
      key: `${e.ts}_${e.afterHash}`,
      ts: e.ts,
      tsFmt: fmtTs(e.ts),
      userId: e.userId,
      userLabel: u ? `${u.name} (${u.label || u.level})` : e.userId,
      scope: e.scope,
      fieldsChanged: Array.isArray(e.fieldsChanged) ? e.fieldsChanged : [],
      beforeHash: e.beforeHash,
      afterHash: e.afterHash,
      isNoop: e.beforeHash === e.afterHash,
    };
  }), [entries, usersById]);

  // Phase A2: title + ViewerChip + cross-page links now live in the global
  // AppTopbar / AppSidebar — sidebar is the navigation surface, no need
  // for inline back-links anymore.
  // Phase B1: outer wrapper graduated to <Page width="wide"> (table view).
  return (
    <Page width="wide">
      <p style={{ color: "var(--text-muted)", marginTop: 0, marginBottom: "var(--space-4)", fontSize: "var(--fs-body-sm)" }}>
        {lang === "it"
          ? "Ogni modifica a stato/utente è registrata qui. Append-only, scritto lato server. Gli hash sono FNV-1a a 8 caratteri del JSON prima/dopo — stesso hash = nessun cambiamento."
          : "Every state/user mutation is recorded here. Append-only, written server-side. Hashes are 8-char FNV-1a of the before/after JSON — identical hash = no change."}
      </p>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "var(--space-3)" }}>
        <span style={{ color: "var(--text-muted)", fontSize: "var(--fs-body-sm)" }}>
          {lang === "it"
            ? `${entries.length} ${entries.length === 1 ? "voce" : "voci"}${exhausted ? " (tutte caricate)" : ""}`
            : `${entries.length} entr${entries.length === 1 ? "y" : "ies"}${exhausted ? " (all loaded)" : ""}`}
        </span>
        <Button variant="ghost" size="sm" onClick={loadInitial} disabled={busy} loading={busy}>
          {busy ? (lang === "it" ? "Caricamento…" : "Loading…") : (lang === "it" ? "↻ Aggiorna" : "↻ Refresh")}
        </Button>
      </div>

      {/* Phase 3e — section-level error/empty states use primitives. */}
      {loadError && (
        <ErrorState
          lang={lang}
          title={lang === "it" ? "Impossibile caricare le voci" : "Couldn't load audit entries"}
          message={loadError}
          onRetry={loadInitial}
          retryLabel={lang === "it" ? "Riprova" : "Retry"}
        />
      )}

      {busy && entries.length === 0 && !loadError && (
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)", padding: "var(--space-2) 0" }}>
          <Skeleton variant="block" height={36} />
          <Skeleton variant="block" height={36} />
          <Skeleton variant="block" height={36} />
        </div>
      )}

      {entries.length === 0 && !busy && !loadError && (
        <EmptyState
          title={lang === "it" ? "Nessuna voce di registro" : "No audit entries yet"}
          message={lang === "it"
            ? "Fai una modifica in /admin/users o salva i numeri AE per popolare."
            : "Make a change in /admin/users or save AE numbers to populate."}
        />
      )}

      {entries.length > 0 && (
        // Phase 4b audit — DR-23: route through the v2 DataTable primitive
        // (compact density, no sticky header — log doesn't need pinning).
        // The wrapper enforces row hover, focus rings, and v2-table chrome;
        // internal markup stays unchanged so per-row scope/no-op semantics
        // (Phase 3d) survive the migration.
        <DataTable density="compact" stickyHeader={false}>
          <thead>
            <tr>
              <th>{lang === "it" ? "Quando" : "When"}</th>
              <th>{lang === "it" ? "Chi" : "Who"}</th>
              <th>{lang === "it" ? "Ambito" : "Scope"}</th>
              <th>{lang === "it" ? "Campi" : "Fields"}</th>
              <th style={{ fontFamily: "var(--font-mono)" }}>{lang === "it" ? "Prima → Dopo" : "Before → After"}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              // Phase 3d — dropped the row-level opacity:0.5 for no-op rows.
              // The new "no change" tag in the hash cell carries the signal
              // explicitly; dimming the whole row was hiding the timestamp +
              // operator info that's still useful for auditing.
              <tr key={r.key}>
                  {/* Phase 4b audit (DR-14) — dropped the tr-level native
                      title= hint. The "no change" tag in the hash cell
                      below already carries the signal explicitly (Phase
                      3d), so the row-level tooltip was redundant. */}
                <td style={{ whiteSpace: "nowrap" }}>{r.tsFmt}</td>
                <td style={{ whiteSpace: "nowrap" }}>{r.userLabel}</td>
                <td style={{ whiteSpace: "nowrap", color: SCOPE_TONE[r.scope] || "var(--text-primary)" }}>
                  {/* Phase 3d — icon + text pairing so the scope signal
                      isn't color-only. Icon stroke inherits currentColor. */}
                  <span style={{ display: "inline-flex", alignItems: "center", gap: "var(--space-1)" }}>
                    {scopeIcon(r.scope)}
                    {r.scope}
                  </span>
                </td>
                <td>
                  {r.fieldsChanged.length === 0
                    ? <span style={{ color: "var(--text-muted)" }}>—</span>
                    : r.fieldsChanged.join(", ")}
                </td>
                <td style={{ fontFamily: "var(--font-mono)", whiteSpace: "nowrap" }}>
                  <span style={{ color: "var(--text-muted)" }}>{r.beforeHash}</span>
                  <span style={{ margin: "0 var(--space-1)", color: "var(--text-muted)" }}>→</span>
                  <span style={{ color: r.isNoop ? "var(--text-muted)" : "var(--text-primary)" }}>{r.afterHash}</span>
                  {r.isNoop && (
                    /* Phase 3d — explicit "no change" tag with icon, replaces
                       the opacity-only dim that was ambiguous (audit §4). */
                    <span style={{ display: "inline-flex", alignItems: "center", gap: "var(--space-0-5)", marginLeft: "var(--space-2)", color: "var(--text-muted)", fontFamily: "var(--font-sans)", fontSize: "var(--fs-caption)", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                      <Minus size={12} /> {lang === "it" ? "nessuna modifica" : "no change"}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </DataTable>
      )}

      {entries.length > 0 && !exhausted && (
        <div style={{ display: "flex", justifyContent: "center", marginTop: "var(--space-4)" }}>
          <Button variant="ghost" size="md" onClick={loadOlder} disabled={busy} loading={busy}>
            {busy ? (lang === "it" ? "Caricamento…" : "Loading…") : (lang === "it" ? "Carica più vecchi" : "Load older")}
          </Button>
        </div>
      )}
    </Page>
  );
}
